// src/pages/AddNewPartner.jsx
import React, { useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiPost, withAuth } from "../utils/apiClient";

const AddNewPartner = () => {
	const { token, role } = useAuth();

	const [form, setForm] = useState({
		partnerName: "",
		email: "",
		password: ""
	});

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const isAdmin = role === "fl-admin";

	const canSubmit = useMemo(() => {
		const partnerName = String(form.partnerName || "").trim();
		const email = String(form.email || "").trim();
		const password = String(form.password || "");
		return isAdmin && !!token && partnerName && email && password && !loading;
	}, [form, isAdmin, token, loading]);

	const onChange = (e) => {
		const { name, value } = e.target;
		setForm((p) => ({ ...p, [name]: value }));
		setError("");
		setSuccess("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!canSubmit) return;

		setLoading(true);
		setError("");
		setSuccess("");

		try {
			const payload = {
				partnerName: String(form.partnerName || "")
					.trim()
					.toLowerCase(),
				email: String(form.email || "")
					.trim()
					.toLowerCase(),
				password: form.password
			};

			// ✅ Admin-only API call with Bearer token
			await apiPost("/auth/signup", {
				headers: withAuth(token),
				body: payload
			});

			setSuccess("Partner created successfully ✅");
			setForm({ partnerName: "", email: "", password: "" });
		} catch (err) {
			setError(err?.message || "Something went wrong");
		} finally {
			setLoading(false);
		}
	};

	// Hard stop: if someone somehow reaches component without admin role,
	// never show the form / never call API.
	if (!isAdmin) {
		return (
			<section className="rounded-lg bg-white p-6 shadow-sm max-w-3xl">
				<div className="text-sm text-slate-700">
					You don’t have permission to access this page.
				</div>
			</section>
		);
	}

	return (
		<section className="rounded-lg bg-white p-6 shadow-sm max-w-3xl">
			<form
				onSubmit={handleSubmit}
				className="space-y-4"
			>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-slate-700">
							Partner Name
						</label>
						<input
							name="partnerName"
							value={form.partnerName}
							onChange={onChange}
							className="mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="small letter.no blank space."
							required
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-slate-700">
							Email
						</label>
						<input
							type="email"
							name="email"
							value={form.email}
							onChange={onChange}
							className="mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="partner@domain.com"
							required
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-slate-700">
							Password
						</label>
						<input
							type="password"
							name="password"
							value={form.password}
							onChange={onChange}
							className="mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							required
						/>
					</div>
				</div>

				{error && (
					<div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
						{error}
					</div>
				)}

				{success && (
					<div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
						{success}
					</div>
				)}

				<div className="flex justify-end">
					<button
						type="submit"
						disabled={!canSubmit}
						className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
					>
						{loading ? "Creating..." : "Create Partner"}
					</button>
				</div>
			</form>
		</section>
	);
};

export default AddNewPartner;
